import React, { useEffect } from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { gsap, Power3 } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger.js'

import DropHeader from '../shared/DropHeader'
import { colors } from '../../styles/variables'
import { media } from '../../styles/util'
import { Headline, LargeP } from '../shared/text'

const Section = styled.section`
  width: 100%;
  background-color: ${colors.teal};
  position: relative;
  ${media.mobile`
    padding: 0 25px;
  `}
  ${media.tablet`
    padding: 0 30px;
  `}

  h1, p {
    color: ${colors.navy};
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  margin: 0 auto;
  ${media.mobile`
    height: 275px;
  `}
  ${media.tablet`
    height: 400px;
  `}
  ${media.desktop`
    height: 515px;
    max-width: 1300px;
    margin-top: -40px;
  `}
`

const Grid = styled.div`
  margin: 0 auto;
  width: 100%;
  display: grid;
  justify-content: space-around;

  ${media.mobile`
    grid-template-columns: 1fr;
    grid-row-gap: 50px;
    padding: 50px 0 60px 0;
  `}
  ${media.tablet`
    grid-template-columns: 550px;
    grid-row-gap: 50px;
    padding: 50px 0 80px 0;
  `}
  ${media.desktop`
    max-width: 1100px;
    grid-template-columns: repeat(2, 428px);
    grid-column-gap: 80px;
    padding: 70px 40px 100px 40px;
  `}

  h1 {
    color: ${colors.navy};
  }
`

const TextHeader = styled(Headline)`
  margin-bottom: 30px;

  ${media.desktop`
    font-size: 83px;
  `}
`

const query = graphql`
  query {
    image: file(relativePath: { regex: "/homepage/finn-way.jpg/" }) {
      childImageSharp {
        gatsbyImageData(width: 1100)
      }
    }
  }
`

const TheFinnWay = () => {
  const { image } = useStaticQuery(query)

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)
    gsap.fromTo(
      '.textHeader',
      { y: 20, opacity: 0 },
      { scrollTrigger: '.textHeader', duration: 1, ease: Power3.easeOut, y: 0, opacity: 1 }
    )
    gsap.fromTo(
      '.textBody',
      { y: 20, opacity: 0 },
      {
        scrollTrigger: '.textBody',
        delay: 0.25,
        duration: 1,
        ease: Power3.easeOut,
        y: 0,
        opacity: 1
      }
    )
  }, [])

  return (
    <Section>
      <DropHeader title="The Finn Way" titleMobile={`The\nFinn Way`} />
      <ImageWrapper>
        <GatsbyImage
          image={getImage(image)}
          alt="Girl and dog"
          style={{ height: '100%', width: '100%' }}
          imgStyle={{
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
      </ImageWrapper>
      <Grid>
        <div>
          <TextHeader className="textHeader">The Finn Formula</TextHeader>
          <LargeP className="textBody">
            Finding the right pup-care solution can feel trickier than teaching your dog to
            moon-walk, juggle, and file taxes at once. Finn makes it easy—pairing rock-solid science
            with drool-worthy natural ingredients so your best friend can live large and wag longer.
          </LargeP>
        </div>
        <div>
          <TextHeader className="textHeader">Only the Good Stuff</TextHeader>
          <LargeP className="textBody">
            We chase down premium, proven ingredients as eagerly as your dog chases the ball. Each
            one earns its spot through rigorous vet scrutiny and a strict no-junk policy—zero
            artificials, zero fillers, 100 % paws-itive purpose.
          </LargeP>
        </div>
      </Grid>
    </Section>
  )
}

export default TheFinnWay
